import { useState, type FormEvent } from "react";
import Reveal, { useReveal } from "../motion/Reveal";
import linkedinUrl from "../assets/connect/icon-linkedin.svg";
import emailUrl from "../assets/connect/icon-email.svg";
import webUrl from "../assets/connect/icon-web.svg";
import "./ConnectPage.css";

const EMAIL = "schusslera@gmail.com";
const FORM_NAME = "portfolio-contact";

type Status = "idle" | "sending" | "success" | "error";
type Errors = { name?: string; email?: string; message?: string };

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <span className="connect-error" id={id}>
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 4.75v3.6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="8" cy="11.15" r="0.75" fill="currentColor" />
      </svg>
      {message}
    </span>
  );
}

function handledByNetlify(response: Response) {
  const server = response.headers.get("server") ?? "";
  return (
    response.headers.has("x-nf-request-id") ||
    server.toLowerCase().includes("netlify")
  );
}

export default function ConnectPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [gotcha, setGotcha] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>("idle");
  const intro = useReveal<HTMLDivElement>(2, "stagger");
  const formMotion = useReveal<HTMLFormElement>(3, "rise");

  function validate() {
    const next: Errors = {};
    if (!name.trim()) next.name = "Please enter your name.";
    if (!email.trim()) next.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      next.email = "Please enter a valid email address.";
    }
    if (!message.trim()) next.message = "Please enter a message.";
    return next;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "sending") return;
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) {
      setStatus("idle");
      return;
    }
    if (gotcha) return;

    setStatus("sending");
    try {
      const body = new URLSearchParams({
        "form-name": FORM_NAME,
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        "bot-field": "",
      });
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!response.ok || !handledByNetlify(response)) {
        setStatus("error");
        return;
      }
      setName("");
      setEmail("");
      setMessage("");
      setErrors({});
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  return (
    <div className="connect-page" id="connect">
      <main>
        <div className="connect-layout">
          <div className="connect-copy">
              <header className="connect-heading">
                <Reveal>
                  <p className="section-kicker">
                    <span>05</span>
                    <span className="section-rule section-rule-long" aria-hidden="true" />
                  </p>
                </Reveal>
              </header>
              <div className="connect-follow">
                <Reveal variant="editorial" delay={1}>
                  <h1>Connect</h1>
                </Reveal>
                <Reveal delay={2}>
                  <p className="connect-subtitle">Let’s build something great together</p>
                </Reveal>
                <div
                  ref={intro.ref}
                  className={`connect-intro ${intro.className}`}
                  style={intro.style}
                >
                <a
                  className="connect-icon"
                  href="https://www.linkedin.com/in/schusslera/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src={linkedinUrl} alt="" width={48} height={48} />
                  <span className="connect-sr">Arielle Schussler on LinkedIn</span>
                </a>
                <p>
                  Are you a living, breathing human? Let&apos;s chat.
                  <br />
                  Do you also want to talk about new opportunities, design, the
                  meaning of life, and/or the best cookie you&apos;ve ever had?
                  Even better.
                </p>
                <a className="connect-icon" href={`mailto:${EMAIL}`}>
                  <img src={emailUrl} alt="" width={48} height={48} />
                  <span className="connect-sr">Email {EMAIL}</span>
                </a>
                <p>You can reach me by email, on LinkedIn, or through the form.</p>
                <a
                  className="connect-icon"
                  href="https://www.arielleschussler.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src={webUrl} alt="" width={48} height={48} />
                  <span className="connect-sr">arielleschussler.com</span>
                </a>
                <p>
                  I have no chill, so I&apos;ll probably respond sooner than
                  you&apos;d expect.
                  <br />
                  Looking forward to meeting you!
                </p>
                </div>
              </div>
          </div>

          <form
            ref={formMotion.ref}
            className={`connect-form ${formMotion.className}`}
            style={formMotion.style}
            name={FORM_NAME}
            method="POST"
            action="/"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={onSubmit}
            noValidate
          >
              <div className="connect-fields">
                <label>
                  <span className="connect-sr">Name</span>
                  <input
                    name="name"
                    type="text"
                    autoComplete="name"
                    placeholder="Name"
                    value={name}
                    required
                    aria-required="true"
                    aria-invalid={errors.name ? true : undefined}
                    aria-describedby={errors.name ? "connect-name-error" : undefined}
                    onChange={(event) => setName(event.target.value)}
                  />
                  <FieldError id="connect-name-error" message={errors.name} />
                </label>
                <label>
                  <span className="connect-sr">Email</span>
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="Email"
                    value={email}
                    required
                    aria-required="true"
                    aria-invalid={errors.email ? true : undefined}
                    aria-describedby={errors.email ? "connect-email-error" : undefined}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  <FieldError id="connect-email-error" message={errors.email} />
                </label>
                <label className="connect-message">
                  <span className="connect-sr">Message</span>
                  <textarea
                    name="message"
                    placeholder="Message"
                    value={message}
                    required
                    aria-required="true"
                    aria-invalid={errors.message ? true : undefined}
                    aria-describedby={errors.message ? "connect-message-error" : undefined}
                    onChange={(event) => setMessage(event.target.value)}
                  />
                  <FieldError id="connect-message-error" message={errors.message} />
                </label>
              </div>
              <label className="connect-gotcha" aria-hidden="true">
                Company
                <input
                  name="bot-field"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={gotcha}
                  onChange={(event) => setGotcha(event.target.value)}
                />
              </label>
              <button type="submit" disabled={status === "sending"}>
                {status === "sending" ? "Sending..." : "Send"}
              </button>
              <p
                className="connect-status"
                data-state={status === "success" || status === "error" ? status : undefined}
                role="status"
                aria-live="polite"
              >
                {status === "success" ? (
                  <>
                    <svg viewBox="0 0 16 16" aria-hidden="true">
                      <path
                        d="M3.25 8.15 6.4 11.2 12.75 4.8"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>Message sent! I&apos;ll be in touch soon.</span>
                  </>
                ) : null}
                {status === "error" ? (
                  <>
                    <svg viewBox="0 0 16 16" aria-hidden="true">
                      <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M8 4.75v3.6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="8" cy="11.15" r="0.75" fill="currentColor" />
                    </svg>
                    <span>
                      Something went wrong. Please try again or{" "}
                      <a href={`mailto:${EMAIL}`}>email me directly</a>.
                    </span>
                  </>
                ) : null}
              </p>
            </form>
        </div>
      </main>
    </div>
  );
}
